<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&#8230;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Marcas de productos</h1>
          <p class="view-header__subtitle">Administrá las marcas para usarlas en los productos.</p>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscador
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="filters.qname" class="form-control" placeholder="Buscar marca...">
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- TABLA -->
      <div class="col-md-8">
        <div class="data-table">
          <div class="table-responsive">
            <table class="table mb-0">
              <thead>
                <tr>
                  <th>
                    <a href="#" @click.prevent="sortList('name')" class="data-table__sort" :class="{ 'is-active': filters.sorting.col === 'name' }">
                      Marca
                      <i :class="sortIcon('name')"></i>
                    </a>
                  </th>
                  <th>Descripción</th>
                  <th class="text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in paginatedBrands" :key="b.id">
                  <td>
                    <span class="data-table__primary">{{ b.name }}</span>
                  </td>
                  <td class="data-table__muted">{{ b.description }}</td>
                  <td class="text-right">
                    <div class="table-action-group">
                      <button class="table-action-btn" type="button" title="Editar" @click="getBrand(b)">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button class="table-action-btn" type="button" title="Eliminar" @click="deleteBrand(b)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="totalItems === 0">
                  <td colspan="3" class="data-table__empty">No se encontraron marcas</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- PAGINACION -->
          <div class="data-table__footer">
            <div class="data-table__info">
              Mostrando {{ totalItems === 0 ? 0 : (filters.page - 1) * filters.itemsPerPage + 1 }}
              a {{ Math.min(filters.page * filters.itemsPerPage, totalItems) }}
              de {{ totalItems }} items.
            </div>

            <ul class="data-table__pagination">
              <li class="page-item" :class="{ 'disabled': filters.page === 1 }">
                <a class="page-link" href="#" @click.prevent="prevPage()">
                  <i class="bi bi-chevron-left"></i>
                </a>
              </li>
              <li v-for="index in totalPages" :key="index" class="page-item" :class="{ 'active': filters.page === index }">
                <a class="page-link" href="#" @click.prevent="changePage(index)">{{ index }}</a>
              </li>
              <li class="page-item" :class="{ 'disabled': filters.page >= totalPages }">
                <a class="page-link" href="#" @click.prevent="nextPage()">
                  <i class="bi bi-chevron-right"></i>
                </a>
              </li>
            </ul>

            <div class="data-table__page-size">
              <q-select v-model.number="filters.itemsPerPage" :options="itemsPerPageOptions" />
            </div>
          </div>
        </div>
      </div>

      <!-- FORMULARIO -->
      <div class="col-md-4">
        <div class="form-card">
          <div class="form-card__header">
            <h6 class="form-card__title">
              <i :class="brand.id == null ? 'bi bi-plus-lg' : 'bi bi-pencil'"></i>
              {{ brand.id == null ? 'Nueva marca' : 'Editar marca' }}
            </h6>
            <button v-if="brand.id != null" type="button" class="form-card__btn" @click="resetForm()">
              <i class="bi bi-plus-lg"></i>
              Nueva
            </button>
          </div>
          <div class="form-card__body">
            <form @submit.prevent>
              <div class="form-card__field">
                <label class="form-card__label" for="brand-name">Nombre</label>
                <input
                  id="brand-name"
                  v-model.trim="brand.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': nameState === false }"
                  placeholder="Nombre de la marca"
                  aria-describedby="brand-name-feedback"
                />
                <div v-if="nameState === false" id="brand-name-feedback" class="form-card__error">
                  El nombre debe tener un mínimo de 3 letras
                </div>
                <div v-else class="form-card__hint">Campo obligatorio</div>
              </div>
              <div class="form-card__field">
                <label class="form-card__label" for="brand-description">Descripción</label>
                <input
                  id="brand-description"
                  v-model.trim="brand.description"
                  type="text"
                  class="form-control"
                  placeholder="Descripción de la marca"
                />
              </div>
            </form>
          </div>
          <div class="form-card__footer">
            <button type="button" class="form-card__btn form-card__btn--primary" :disabled="!canSave || isLoading" @click="saveBrand()">
              <i class="bi bi-check-lg"></i>
              Guardar marca
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import QSelect from '@/components/shared/QSelect.vue';

const emptyBrand = () => ({ id: null, name: '', description: '' });

export default {
  components: { QSelect },
  data() {
    return {
      isLoading: false,
      brand: emptyBrand(),
      filters: {
        qname: '',
        page: 1,
        itemsPerPage: 15,
        sorting: { col: 'name', order: 'asc' },
      },
    };
  },
  computed: {
    ...mapState('brands', { brands: state => state.brands }),
    itemsPerPageOptions() {
      return [
        { value: 15, label: '15 por página' },
        { value: 25, label: '25 por página' },
        { value: 50, label: '50 por página' },
      ];
    },
    brandList() {
      return Array.isArray(this.brands) ? this.brands : [];
    },
    filteredBrands() {
      const q = (this.filters.qname || '').trim().toLowerCase();
      let list = this.brandList.slice();
      if (q) {
        list = list.filter(b =>
          (b.name || '').toLowerCase().includes(q) ||
          (b.description || '').toLowerCase().includes(q)
        );
      }
      const { col, order } = this.filters.sorting;
      if (col) {
        list.sort((a, b) => {
          const av = (a[col] || '').toString().toLowerCase();
          const bv = (b[col] || '').toString().toLowerCase();
          if (av < bv) return order === 'asc' ? -1 : 1;
          if (av > bv) return order === 'asc' ? 1 : -1;
          return 0;
        });
      }
      return list;
    },
    totalItems() { return this.filteredBrands.length; },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.filters.itemsPerPage));
    },
    paginatedBrands() {
      const start = (this.filters.page - 1) * this.filters.itemsPerPage;
      return this.filteredBrands.slice(start, start + this.filters.itemsPerPage);
    },
    nameState() {
      const len = this.brand.name ? this.brand.name.length : 0;
      if (len === 0) return null;
      return len >= 3 ? null : false;
    },
    canSave() {
      return !!(this.brand.name && this.brand.name.trim().length >= 3);
    },
  },
  created() {
    this.$store.dispatch('brands/getBrands');
  },
  watch: {
    'filters.qname'() { this.filters.page = 1; },
    'filters.itemsPerPage'() { this.filters.page = 1; },
    'filters.sorting.col'() { this.filters.page = 1; },
    'filters.sorting.order'() { this.filters.page = 1; },
    totalPages(newVal) {
      if (this.filters.page > newVal) this.filters.page = newVal;
    },
  },
  methods: {
    resetForm() {
      this.brand = emptyBrand();
    },
    sortList(col) {
      if (this.filters.sorting.col === col) {
        this.filters.sorting.order = this.filters.sorting.order === 'asc' ? 'desc' : 'asc';
      } else {
        this.filters.sorting.col = col;
        this.filters.sorting.order = 'asc';
      }
    },
    sortIcon(col) {
      if (this.filters.sorting.col !== col) return 'bi bi-chevron-expand';
      return this.filters.sorting.order === 'asc' ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },
    changePage(page) { this.filters.page = page; },
    prevPage() { if (this.filters.page > 1) this.filters.page--; },
    nextPage() { if (this.filters.page < this.totalPages) this.filters.page++; },
    getBrand(b) {
      this.brand = { ...b };
    },
    deleteBrand(b) {
      if (this.isLoading) return;
      if (!confirm('¿Desea borrar esta marca?')) return;
      this.isLoading = true;
      this.$http.post('/api/brands/delete', { id: b.id })
        .then(response => {
          if (response.data.code == 201) {
            this.$store.dispatch('brands/getBrands');
            if (this.brand.id === b.id) this.resetForm();
          } else {
            alert('No se pudo borrar la marca.');
          }
        })
        .catch(error => {
          console.error('deleteBrand:', error);
          alert('No se pudo borrar la marca.');
        })
        .finally(() => { this.isLoading = false; });
    },
    saveBrand() {
      if (!this.canSave || this.isLoading) return;
      this.isLoading = true;
      const req = this.brand.id != null
        ? this.$http.post('/api/brands/edit/' + this.brand.id, this.brand)
        : this.$http.post('/api/brands/add', this.brand);
      req.then(response => {
        if (response.data && response.data.code == 201) {
          this.$store.dispatch('brands/getBrands');
          this.resetForm();
        } else {
          alert('No se pudo guardar la marca.');
        }
      })
      .catch(error => {
        console.error('saveBrand:', error);
        alert('No se pudo guardar la marca.');
      })
      .finally(() => { this.isLoading = false; });
    },
  },
};
</script>
